import { lazy, ReactElement } from "react";
import ProtectedRouteOrganizer from "./ProtectedRouteOrgaizer";
import PublicRouteOrgaizer from "./PublicRouteOrganizer";
import StoreModeGuard from "./StoreModeGuard";

const AddExtraItems = lazy(() => import("../components/store/products/form/ExtraItemsForm"));
const OnwerDetailsPageForAdmin = lazy(() => import("../pages/admin/owners/OnweDetailsPageForAdmin"));
const StoreDetailPage = lazy(() => import("../pages/admin/stores/StoreDetailPage"));
const AdminStoreNotesPage = lazy(() => import("../pages/admin/stores/AdminStoreNotesPage"));
const OrgaizerSendOtpPage = lazy(() => import("../pages/organizer/auth/OrganizerSendOtpPage"));
const OrganizerVerifyOtpPage = lazy(() => import("../pages/organizer/auth/OrganizerVerifyOtpPage"));
const OrganizerEventOwnersPage = lazy(() => import("../pages/organizer/events/OrganizerEventOwnersPage"));
const OrganizerHomePage = lazy(() => import("../pages/organizer/OrganizerHomePage"));
const OwnerHomePage = lazy(() => import("../pages/owner/home/OwnerHomePage"));
const CreateStoreWizard = lazy(() => import("../pages/owner/store/CreateStoreWizard"));
const CreateCategoryPage = lazy(() => import("../pages/store/category/CreateCategoryPage"));
const StoreCategoriesOverView = lazy(() => import("../pages/store/category/StoreCategoriesOverView"));
const AddOfferPage = lazy(() => import("../pages/store/offers/CreateOfferPage"));
const StoreOffersOverView = lazy(() => import("../pages/store/offers/StoreOffersOverView"));
const OrderDetailsPage = lazy(() => import("../pages/store/orders/OrderDetailsPage"));
const StoreOrdersOverView = lazy(() => import("../pages/store/orders/StoreOrdersOverView"));
const AddProduct = lazy(() => import("../pages/store/products/AddProduct"));
const AddProductInstructions = lazy(() => import("../pages/store/products/AddProductInstructions"));
const AddProductVariant = lazy(() => import("../pages/store/products/AddProductVariant"));
const ProductDetailsPage = lazy(() => import("../pages/store/products/ProductDetailsPage"));
const StoreProductsOverView = lazy(() => import("../pages/store/products/StoreProductsOverView"));
const UpdateProduct = lazy(() => import("../pages/store/products/UpdateProduct"));
const UploadextraItemsPage = lazy(() => import("../pages/store/products/UploadExtraItems"));
const UploadInstructionsPage = lazy(() => import("../pages/store/products/UploadInstructions"));
const UploadProducts = lazy(() => import("../pages/store/products/UploadProducts"));
const UploadVariantsPage = lazy(() => import("../pages/store/products/UploadVariants"));
const CreateBookingServicePage = lazy(() => import("../pages/store/bookingServices/CreateBookingServicePage"));
const StoreBookingDetailsPage = lazy(() => import("../pages/store/bookings/StoreBookingDetailsPage"));
const StoreBookingServicesOverView = lazy(() => import("../pages/store/bookingServices/StoreBookingServicesOverView"));
const StoreReviewOverView = lazy(() => import("../pages/store/review/StoreReviewOverView"));
const StoreStaffOverView = lazy(() => import("../pages/store/staff/StoreStaffOverView"));
const StoreOverviewTransactions = lazy(() => import("../pages/store/transactions/StoreOverviewTransactions"));
const UpdateStore = lazy(() => import("../pages/store/updatePages.tsx/UpdateStore"));
const StoreAddVariantCategoryPage = lazy(() => import("../pages/store/variantCategories/StoreAddVariantCategory"));
const StoreViewVariantCategoriesPage = lazy(() => import("../pages/store/variantCategories/StoreViewVariantCategories"));
const OrganizerCreateOwnerPage = lazy(() => import("../pages/organizer/owners").then(m => ({ default: m.OrganizerCreateOwnerPage })));
const OrganizerEventStoresPage = lazy(() => import("../pages/organizer/events/OrganizerEventStoresPage"));
const StoreLaundryServicesOverView = lazy(() => import("../pages/store/laundryServices/StoreLaundryServicesOverView"));
const CreateLaundryServicePage = lazy(() => import("../pages/store/laundryServices/CreateLaundryServicePage"));
const LaundryServiceDetailsPage = lazy(() => import("../pages/store/laundryServices/LaundryServiceDetailsPage"));

const withOrderFeatureGuard = (element: ReactElement) => (
    <StoreModeGuard requireOrderFeatures>{element}</StoreModeGuard>
);

const withBookingFeatureGuard = (element: ReactElement) => (
    <StoreModeGuard requireBookingFeatures>{element}</StoreModeGuard>
);

const withLaundryFeatureGuard = (element: ReactElement) => (
    <StoreModeGuard requireLaundryFeatures>{element}</StoreModeGuard>
);

export const organizerRoutes = [
    { path: "/organizer/send-otp", element: <PublicRouteOrgaizer><OrgaizerSendOtpPage /></PublicRouteOrgaizer> },
    { path: "/organizer/verify-otp", element: <PublicRouteOrgaizer><OrganizerVerifyOtpPage /></PublicRouteOrgaizer> },
    { path: "/organizer", element: <ProtectedRouteOrganizer><OrganizerHomePage /></ProtectedRouteOrganizer> },
    { path: "/organizer/events/owners/:eventId", element: <ProtectedRouteOrganizer><OrganizerEventOwnersPage /></ProtectedRouteOrganizer> },
    { path: "/organizer/events/owners/:eventId/add", element: <ProtectedRouteOrganizer><OrganizerCreateOwnerPage /></ProtectedRouteOrganizer> },
    { path: "/organizer/events/stores/:eventId", element: <ProtectedRouteOrganizer><OrganizerEventStoresPage /></ProtectedRouteOrganizer> },

    {
        path: "/organizer/owners/:ownerId", element: <ProtectedRouteOrganizer>
            <OnwerDetailsPageForAdmin />
        </ProtectedRouteOrganizer>
    },

    {
        path: "/organizer/owners/:ownerId/add-store/wizard", element: <ProtectedRouteOrganizer>
            <CreateStoreWizard />
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId", element: <ProtectedRouteOrganizer>
            <StoreDetailPage />
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/statistics", element: <ProtectedRouteOrganizer>
            <OwnerHomePage />
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/transactions", element: <ProtectedRouteOrganizer>
            <StoreOverviewTransactions />
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/reviews", element: <ProtectedRouteOrganizer>
            <StoreReviewOverView />
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/staff", element: <ProtectedRouteOrganizer>
            {withBookingFeatureGuard(<StoreStaffOverView />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/booking-services", element: <ProtectedRouteOrganizer>
            {withBookingFeatureGuard(<StoreBookingServicesOverView />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/booking-services/create", element: <ProtectedRouteOrganizer>
            {withBookingFeatureGuard(<CreateBookingServicePage />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/laundry-services", element: <ProtectedRouteOrganizer>
            {withLaundryFeatureGuard(<StoreLaundryServicesOverView />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/laundry-services/create", element: <ProtectedRouteOrganizer>
            {withLaundryFeatureGuard(<CreateLaundryServicePage />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/laundry-services/:serviceId", element: <ProtectedRouteOrganizer>
            {withLaundryFeatureGuard(<LaundryServiceDetailsPage />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/offers", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<StoreOffersOverView />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/offers/add-offer", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<AddOfferPage />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/variant-categories", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<StoreViewVariantCategoriesPage />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/add-variantCategory", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<StoreAddVariantCategoryPage />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/categories", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<StoreCategoriesOverView />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/add-category", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<CreateCategoryPage />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/orders", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<StoreOrdersOverView />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/orders/:orderId", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<OrderDetailsPage />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/bookings/:bookingId", element: <ProtectedRouteOrganizer>
            {withBookingFeatureGuard(<StoreBookingDetailsPage />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/settings", element: <ProtectedRouteOrganizer>
            <UpdateStore />
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/notes", element: <ProtectedRouteOrganizer>
            <AdminStoreNotesPage />
        </ProtectedRouteOrganizer>
    },

    {
        path: "/organizer/stores/:storeId/products", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<StoreProductsOverView />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/add-product", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<AddProduct />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/products/:productId/extra-items", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<AddExtraItems />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/products/:productId/instructions", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<AddProductInstructions />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/products/:productId/variants", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<AddProductVariant />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/products/:productId", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<ProductDetailsPage />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/update-product/:productId", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<UpdateProduct />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/upload-product", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<UploadProducts />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/upload-extras", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<UploadextraItemsPage />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/upload-instructions", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<UploadInstructionsPage />)}
        </ProtectedRouteOrganizer>
    },
    {
        path: "/organizer/stores/:storeId/upload-variants", element: <ProtectedRouteOrganizer>
            {withOrderFeatureGuard(<UploadVariantsPage />)}
        </ProtectedRouteOrganizer>
    },
]